import { useCallback, useEffect, useMemo, useState } from 'react'

import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import {
  getResourceDefinition,
  IResourceRuntimeContext,
} from '~/client/src/shared/resources/definitions'
import { ResourcePropertyDto } from '~/client/src/shared/resources/domain/resourceSchema'

import { IResourceRecord } from '../types'
import useColumnConfig from './useColumnConfig'
import { useDatabaseView } from './useDatabaseView'

interface UseTableColumnsParams {
  projectId?: string
  resourceTypeId?: string
  userId?: string
  columnConfigKey: LocalStorageKey
  resourceRuntimeContext: IResourceRuntimeContext
  /** Records backing the table — gate data-dependent default visibility. */
  records?: IResourceRecord[]
  properties: ResourcePropertyDto[]
  schemaLoading: boolean
}

export const useTableColumns = ({
  projectId,
  resourceTypeId,
  userId,
  columnConfigKey,
  resourceRuntimeContext,
  records,
  properties,
  schemaLoading,
}: UseTableColumnsParams) => {
  const [columnWidths, setColumnWidths] = useState<Map<string, number>>(
    new Map(),
  )

  const {
    columns: dbViewColumns,
    behaviorColumns,
    loading,
    fieldCatalog,
    fieldProjector,
  } = useDatabaseView({
    resourceTypeId,
    resourceRuntimeContext,
    properties,
    loading: schemaLoading,
  })

  const tableDefaults = getResourceDefinition(resourceTypeId)?.table

  // A field key reaches the table as either the bare key (static fields) or
  // `data.<key>` (definition and project properties), so resolve against the
  // columns that actually exist and drop keys this project has no column for.
  const toDataKeys = useCallback(
    (keys?: string[]) =>
      keys &&
      dbViewColumns
        .filter(column => keys.includes(column.dataKey.replace(/^data\./, '')))
        .map(column => column.dataKey),
    [dbViewColumns],
  )

  // A conditional column stays hidden until some record actually has a value for
  // it — StruxTag State keeps out of projects that report no tag state.
  const defaultVisibleDataKeys = useMemo(() => {
    const conditionalKeys = tableDefaults?.defaultVisibleWhenPopulatedKeys
    return toDataKeys(
      tableDefaults?.defaultVisibleKeys?.filter(
        key =>
          !conditionalKeys?.includes(key) ||
          !!records?.some(
            record => !fieldProjector.resolve(record, key).isEmpty,
          ),
      ),
    )
  }, [fieldProjector, records, tableDefaults, toDataKeys])

  const defaultFrozenDataKeys = useMemo(
    () => toDataKeys(tableDefaults?.defaultFrozenKeys),
    [tableDefaults, toDataKeys],
  )

  const {
    configuredColumns,
    defaultColumns,
    excludedFromSortKeys,
    resetColumnsConfiguration,
    updateColumnsConfiguration,
    fixedColumnsCount,
  } = useColumnConfig({
    initialColumns: dbViewColumns,
    projectId,
    userId,
    storageKey: columnConfigKey,
    defaultVisibleDataKeys,
    defaultFrozenDataKeys,
  })

  const sortingOptions = useMemo(
    () =>
      fieldCatalog.sortableFields.map(field => ({
        key: field.source === 'static' ? field.key : `data.${field.key}`,
        label: field.label,
        glyph: field.glyph,
      })),
    [fieldCatalog.sortableFields],
  )

  const groupingOptions = useMemo(
    () =>
      fieldCatalog.groupableFields.map(field => ({
        key: field.key,
        label: field.label,
        glyph: field.glyph,
      })),
    [fieldCatalog.groupableFields],
  )

  useEffect(() => {
    setColumnWidths(prev => {
      const newMap = new Map(prev)
      dbViewColumns.forEach(col => {
        if (!newMap.has(col.dataKey) && col.width) {
          newMap.set(col.dataKey, col.width)
        }
      })
      return newMap
    })
  }, [dbViewColumns])

  const onColumnResize = useCallback((columnKey: string, width: number) => {
    setColumnWidths(prev => {
      const newMap = new Map(prev)
      newMap.set(columnKey, width)
      return newMap
    })
  }, [])

  return {
    columns: dbViewColumns,
    configuredColumns,
    defaultColumns,
    sortingOptions,
    groupingOptions,
    behaviorColumns,
    excludedFromSortKeys,
    resetColumnsConfiguration,
    updateColumnsConfiguration,
    fixedColumnsCount,
    columnWidths,
    onColumnResize,
    loading,
    fieldCatalog,
    fieldProjector,
  }
}
